<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import FormField from '@uiKit/components/FormField.vue'
import { useToastStore } from '@uiKit/store/toastStore'
import { useTicketCategoriesQuery } from '../api/queries/useTicketCategoriesQuery'
import { useCreateSupportTicketMutation } from '../api/mutations/useCreateSupportTicketMutation'
import {
  PROPERTY_ISSUE_CATEGORY_CODE,
  PROPERTY_ISSUE_MAX_LENGTH,
  PROPERTY_ISSUE_MIN_LENGTH,
} from '../constants/supportTickets'

// "Report an issue" on Property details (FSD 3.3.2). One field — what is wrong —
// filed as a support ticket under "Property Status". The subject is built from
// the property so the ticket names it without the seller retyping it; the
// reference is what support searches by.
const props = defineProps<{
  propertyName: string
  propertyRefCode: string | null
}>()

const open = defineModel<boolean>({ required: true })

const toastStore = useToastStore()
const categoriesQuery = useTicketCategoriesQuery()
const createMutation = useCreateSupportTicketMutation()

const description = ref('')
const isTouched = ref(false)

watch(open, (isOpen) => {
  if (!isOpen) return
  description.value = ''
  isTouched.value = false
  createMutation.reset()
})

const category = computed(
  () =>
    categoriesQuery.data.value?.find((entry) => entry.code === PROPERTY_ISSUE_CATEGORY_CODE) ??
    null,
)

const length = computed(() => description.value.trim().length)

const validationError = computed(() => {
  if (length.value < PROPERTY_ISSUE_MIN_LENGTH) {
    return `Tell us a little more — at least ${PROPERTY_ISSUE_MIN_LENGTH} characters.`
  }
  if (length.value > PROPERTY_ISSUE_MAX_LENGTH) {
    return `Keep it under ${PROPERTY_ISSUE_MAX_LENGTH.toLocaleString('en')} characters.`
  }
  return ''
})

// A missing category is a setup fault rather than the seller's — say so, and
// point them at the general form instead of letting the submit fail.
const loadError = computed(() => {
  if (categoriesQuery.isError.value) return 'We couldn’t load the form. Try again in a moment.'
  if (categoriesQuery.isSuccess.value && !category.value) {
    return 'Reporting an issue isn’t available right now. Use Contact support instead.'
  }
  return ''
})

const apiError = computed(() => createMutation.error.value?.message ?? '')

const subject = computed(() =>
  [`Property issue`, props.propertyRefCode, props.propertyName].filter(Boolean).join(' · '),
)

function close() {
  if (createMutation.isPending.value) return
  open.value = false
}

function submit() {
  isTouched.value = true
  if (validationError.value || !category.value) return
  createMutation.mutate(
    {
      categoryId: category.value.id,
      subject: subject.value,
      description: description.value,
      attachment: null,
    },
    {
      onSuccess: (referenceCode) => {
        open.value = false
        toastStore.addToast({
          type: 'success',
          message: `Ticket ${referenceCode} submitted. We'll reply by email.`,
        })
      },
    },
  )
}
</script>

<template>
  <Dialog
    :model-value="open"
    header="Report an issue"
    width="520px"
    :dismissable="!createMutation.isPending.value"
    @update:model-value="!$event && close()"
  >
    <Box direction="col" gap="4">
      <Text type="body" color="muted">
        Something on this property looks wrong? Tell us what, and our team will look into it.
      </Text>

      <Message v-if="loadError" color="danger" variant="outlined">{{ loadError }}</Message>

      <FormField
        label="What’s the issue?"
        :error="isTouched ? validationError : ''"
        :helper-text="`${length.toLocaleString('en')} / ${PROPERTY_ISSUE_MAX_LENGTH.toLocaleString('en')} characters`"
      >
        <Textarea
          v-model="description"
          :rows="5"
          placeholder="For example, the floor number shown is incorrect."
          :disabled="createMutation.isPending.value"
          :invalid="isTouched && !!validationError"
          @blur="isTouched = true"
        />
      </FormField>

      <Message v-if="apiError" color="danger" variant="outlined">{{ apiError }}</Message>

      <Box flex gap="3" class="report-issue__actions">
        <Button
          label="Cancel"
          variant="outline"
          :disabled="createMutation.isPending.value"
          @click="close"
        />
        <Button
          label="Submit"
          :loading="createMutation.isPending.value"
          :disabled="!category"
          @click="submit"
        />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.report-issue__actions {
  justify-content: flex-end;
}
</style>
